import { MissionBadge } from '@/features/missions/components/MissionBadge'
import { t } from '@lingui/core/macro'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { useSkipFreeSpeechTask } from '../../api'
import type { FreeSpeechTypeConfig, NectarApiErrorBody, TaskStats } from '../../api/models'
import { LazyTaskFlow } from '../lazy/LazyTaskFlow'
import { type ProjectFlowProps } from '../project-flows'
import { FreeSpeechTaskBody } from './FreeSpeechTaskBody'
import { SkipTaskDialog } from './SkipTaskDialog'
import type { SkipReasonCode } from './skipReasons'

const resolveTypeConfig = (raw: Record<string, unknown> | null | undefined): FreeSpeechTypeConfig => {
	const cfg = raw ?? {}
	const minDurationSec = typeof cfg.minDurationSec === 'number' ? cfg.minDurationSec : 5
	const maxDurationSec = typeof cfg.maxDurationSec === 'number' ? cfg.maxDurationSec : 60
	const maxSkipsPerUser = typeof cfg.maxSkipsPerUser === 'number' ? cfg.maxSkipsPerUser : 0
	return { minDurationSec, maxDurationSec, maxSkipsPerUser }
}

type AxiosLikeError = { response?: { status?: number; data?: NectarApiErrorBody } }

export function FreeSpeechFlow(props: ProjectFlowProps) {
	const { projectId, typeConfig } = props
	const queryClient = useQueryClient()
	const resolvedConfig = resolveTypeConfig(typeConfig)
	const skip = useSkipFreeSpeechTask()

	// Mirror the original lastStatsRef pattern: persist stats across re-renders so the
	// skips badge stays visible after skip-induced query invalidations.
	const lastStatsRef = useRef<TaskStats | null>(null)
	// Separate state drives the progressRightSlot re-render when stats arrive for the first time.
	const [latestStats, setLatestStats] = useState<TaskStats | null>(null)

	const [skipDialogTaskId, setSkipDialogTaskId] = useState<string | null>(null)

	const handleConfirmSkip = (code: SkipReasonCode) => {
		if (!skipDialogTaskId) return
		skip.mutate(
			{ projectId, taskId: skipDialogTaskId, code },
			{
				onSuccess: () => setSkipDialogTaskId(null),
				onError: (err) => {
					const axiosErr = err as AxiosLikeError
					const errorCode = axiosErr.response?.data?.error?.code
					if (axiosErr.response?.status === 403 && errorCode === 'SKIP_LIMIT_EXCEEDED') {
						toast.error(t`No skips remaining`)
						queryClient.invalidateQueries({ queryKey: ['NECTAR_NEXT_TASK', projectId] })
					} else {
						toast.error(t`Could not skip this task`)
					}
					setSkipDialogTaskId(null)
				},
			},
		)
	}

	const statsForDisplay = latestStats ?? lastStatsRef.current

	const skipsSlot =
		resolvedConfig.maxSkipsPerUser > 0 && statsForDisplay ? (
			<MissionBadge
				variant="default"
				tooltip={`You can switch to another task up to ${resolvedConfig.maxSkipsPerUser} times. Skipped tasks won't be shown to you again.`}
			>
				{statsForDisplay.skipsUsed} of {resolvedConfig.maxSkipsPerUser} skips used
			</MissionBadge>
		) : undefined

	return (
		<>
			<LazyTaskFlow
				{...props}
				progressRightSlot={skipsSlot}
				renderBody={({ task, stats }) => {
					// Cache stats for the badge; defer setState to avoid updating during render.
					if (stats && stats !== lastStatsRef.current) {
						lastStatsRef.current = stats
						queueMicrotask(() => setLatestStats(stats))
					}
					return (
						<FreeSpeechTaskBody
							key={task.id}
							task={task}
							taskIndex={props.progress.completed}
							projectId={projectId}
							typeConfig={resolvedConfig}
							stats={stats ?? lastStatsRef.current ?? { completed: 0, target: 0, skipsUsed: 0, skipsRemaining: 0 }}
							captchaEnabled={props.captchaEnabled}
							onSkipRequest={setSkipDialogTaskId}
						/>
					)
				}}
			/>
			<SkipTaskDialog
				open={skipDialogTaskId !== null}
				onOpenChange={(open) => {
					if (!open && !skip.isPending) setSkipDialogTaskId(null)
				}}
				projectId={projectId}
				isSubmitting={skip.isPending}
				onConfirm={handleConfirmSkip}
			/>
		</>
	)
}
